import { uniqBy } from 'es-toolkit'
import { defineStore } from 'pinia'
import { computed, nextTick, ref, toRaw, watch } from 'vue'

import { api } from '@/scripts/api'
import { useCanvasStore } from '@/renderer/core/canvas/canvasStore'
import { useToastStore } from '@/platform/updates/common/toastStore'
import type { ComfyWorkflowJSON } from '@/platform/workflow/validation/schemas/workflowSchema'
import type { ComfyApp } from '@/types'

import { AssetKind } from '../interfaces'
import type { ImageAsset } from '../interfaces/image.interface'
import type { OutputAsset } from '../interfaces/outputAsset.interface'
import type { NordyUser } from '../interfaces/user.interface'
import type { Job } from '../interfaces/job.interface'
import { JobStatus } from '../interfaces/job.interface'
import { fileUrlByAsset } from '../utils/fileBucketUrl'
import { nordyApi } from '../api'
import { useAssetsStore } from '@/stores/assetsStore'
import { useNordyModalStore } from './nordyModalStore'
import { useNordyUserStore } from './nordyUserStore'
import { useNordyWorkflowStore } from './nordyWorkflowStore'

/**
 * Nordy task display status.
 * Extends upstream TaskItemDisplayStatus with Queued and ExecutionTimeout.
 */
export enum NordyTaskDisplayStatus {
  Running = 'Running',
  Pending = 'Pending',
  Queued = 'Queued',
  Completed = 'Completed',
  Failed = 'Failed',
  Cancelled = 'Cancelled',
  ExecutionTimeout = 'ExecutionTimeout'
}

//!nordy - task type with Queued state
type NordyTaskType = 'Running' | 'Pending' | 'Queued' | 'History'

//!nordy - task status
interface NordyTaskStatus {
  status_str: 'success' | 'error' | 'execution_timeout'
  completed: boolean
  messages: Array<[string, Record<string, unknown>]>
}

type NordyAPITaskType = 'queue' | 'history'

export class ResultNordyQueue {
  assetId!: string
  outputAssetId!: string
  assetKind!: string
  type: string = 'output'
  nodeId?: string
  filename?: string
  format?: string
  url?: string
  isPreview?: boolean
  constructor(init: Partial<ResultNordyQueue>) {
    Object.assign(this, init)
  }

  get mediaType(): string {
    switch (this.assetKind) {
      case AssetKind.Image:
        return 'images'
      case AssetKind.Video:
        return 'videos'
      case AssetKind.Audio:
        return 'audios'
      default:
        return 'unknown'
    }
  }

  get isVideo(): boolean {
    return this.assetKind === AssetKind.Video
  }

  get isGif(): boolean {
    return this.url ? this.url.endsWith('.gif') : false
  }

  get isWebp(): boolean {
    return this.url ? this.url.endsWith('.webp') : false
  }
  get isWebm(): boolean {
    return this.url ? this.url.endsWith('.webm') : false
  }

  get isMp4(): boolean {
    return this.url ? this.url.endsWith('.mp4') : false
  }

  get isVideoBySuffix(): boolean {
    return this.isWebm || this.isMp4
  }

  get isImageBySuffix(): boolean {
    return this.isGif || this.isWebp
  }

  get isMp3(): boolean {
    return this.url ? this.url.endsWith('.mp3') : false
  }

  get isWav(): boolean {
    return this.url ? this.url.endsWith('.wav') : false
  }

  get isOgg(): boolean {
    return this.url ? this.url.endsWith('.ogg') : false
  }

  get isFlac(): boolean {
    return this.url ? this.url.endsWith('.flac') : false
  }

  get isAudioBySuffix(): boolean {
    return this.isMp3 || this.isWav || this.isOgg || this.isFlac
  }

  get isAudio(): boolean {
    return (
      this.mediaType === 'audios' ||
      this.isMp3 ||
      this.isWav ||
      this.isOgg ||
      this.isFlac
    )
  }

  get isImage(): boolean {
    return this.mediaType === 'images' || this.isGif || this.isWebp
  }

  get supportsPreview(): boolean {
    return this.isImage || this.isVideo || this.isAudio
  }
}

/** HMR-safe sequence counter: survives hot reloads via globalThis */
const _seqHolder = ((
  globalThis as unknown as Record<string, { value: number }>
).__nordyQueueSeqHolder ??= { value: 0 })

export class NordyQueue {
  static nextSeq() {
    return ++_seqHolder.value
  }

  taskType: NordyTaskType
  createdAt: Date
  seq: number
  id: string
  prompt: {
    workflow: Record<string, unknown> | null
    output: Record<string, unknown> | null
  }
  workflow: Record<string, unknown> | null

  status?: NordyTaskStatus
  jobId?: string | null
  job?: Job | null
  outputs?: ResultNordyQueue[] = []
  flatOutputs?: ReadonlyArray<ResultNordyQueue>
  workflowUid?: string
  original_prompt_id?: string

  get promptId() {
    return this.id
  }

  constructor({
    id,
    taskType,
    workflow,
    prompt,
    status,
    outputs,
    flatOutputs,
    job,
    jobId
  }: {
    id: string
    taskType: NordyTaskType
    workflow: Record<string, unknown> | null
    prompt: {
      workflow: Record<string, unknown> | null
      output: Record<string, unknown> | null
    }
    status?: NordyTaskStatus
    outputs?: ResultNordyQueue[]
    flatOutputs?: ReadonlyArray<ResultNordyQueue>
    job?: Job | null
    jobId?: string | null
  }) {
    this.id = id
    this.taskType = taskType
    this.prompt = prompt
    this.workflow = workflow
    this.status = status
    this.outputs = outputs ?? []
    this.flatOutputs = flatOutputs ?? []
    this.job = job ?? null
    this.jobId = jobId ?? null
    this.createdAt = new Date()
    this.seq = NordyQueue.nextSeq()
  }

  get isPending() {
    return this.taskType === 'Pending'
  }

  get isQueued() {
    return this.taskType === 'Queued'
  }

  get isRunning() {
    return this.taskType === 'Running'
  }

  get isHistory() {
    return this.taskType === 'History'
  }

  get executionTimeInSeconds() {
    return this?.job?.duration ?? 0
  }

  get key() {
    return this.id + this.displayStatus
  }

  get messages() {
    return this.status?.messages || []
  }

  get interrupted() {
    return this.messages.some(
      (message) => message[0] === 'execution_interrupted'
    )
  }

  get displayStatus(): NordyTaskDisplayStatus {
    switch (this.taskType) {
      case 'Pending':
        return NordyTaskDisplayStatus.Pending
      case 'Queued':
        return NordyTaskDisplayStatus.Queued
      case 'Running':
        return NordyTaskDisplayStatus.Running
      case 'History':
        if (this.interrupted) return NordyTaskDisplayStatus.Cancelled

        switch (this.status!.status_str) {
          case 'success':
            return NordyTaskDisplayStatus.Completed
          case 'error':
            return NordyTaskDisplayStatus.Failed
          case 'execution_timeout':
            return NordyTaskDisplayStatus.ExecutionTimeout
        }
    }
  }

  get apiTaskType(): NordyAPITaskType {
    switch (this.taskType) {
      case 'Running':
      case 'Pending':
      case 'Queued':
        return 'queue'
      case 'History':
        return 'history'
    }
  }

  public async loadWorkflow(comfyApp: ComfyApp) {
    if (!this.workflow) {
      return
    }
    await comfyApp.loadGraphData(toRaw(this.workflow) as ComfyWorkflowJSON)
  }

  get previewOutput(): ResultNordyQueue | undefined {
    const preview =
      this.outputs?.find(
        (output) => output.type === 'output' && output.supportsPreview
      ) ?? this.outputs?.find((output) => output.supportsPreview)
    return preview
  }

  public flatten(): NordyQueue[] {
    if (this.displayStatus !== NordyTaskDisplayStatus.Completed) {
      return [this]
    }

    if (!this.outputs) {
      return [this]
    }

    return this.outputs?.map((output, index) => {
      return new NordyQueue({
        id: this.id + '_' + index.toString(),
        taskType: this.taskType,
        workflow: this.workflow,
        prompt: this.prompt,
        jobId: this.jobId,
        job: this.job ?? null,
        status: this.status,
        outputs: [output],
        flatOutputs: [output]
      })
    })
  }
}

//!nordy - reassurance appended to unbilled failure toasts
const NO_CREDIT_CHARGED_NOTICE = 'No credits were charged.'

//!nordy - `noCreditCharged` marks outcomes the user is NOT billed for, so the
// reassurance above can be appended consistently. Timeouts (ExecutionTimeout/
// ForceTimeout) are billed for the consumed GPU slot and intentionally omit it.
type JobErrorMessage = {
  summary: string
  detail: string
  noCreditCharged?: boolean
}

//!nordy - user-friendly error summary/detail by JobStatus
const JOB_ERROR_MESSAGES: Record<string, JobErrorMessage> = {
  [JobStatus.ExecuteError]: {
    summary: 'Generation Failed',
    detail:
      'Something went wrong during generation. Please try again or adjust your workflow.',
    noCreditCharged: true
  },
  [JobStatus.ConnectError]: {
    summary: 'Temporarily Unavailable',
    detail:
      'We could not start your job right now. Please wait a moment and try again.',
    noCreditCharged: true
  },
  [JobStatus.WorkerConnectError]: {
    summary: 'Temporarily Unavailable',
    detail: 'Resources are being prepared. Please wait a moment and try again.',
    noCreditCharged: true
  },
  [JobStatus.InvalidPrompt]: {
    summary: 'Workflow Issue',
    detail:
      'There seems to be an issue with your workflow. Please check the node connections and try again.',
    noCreditCharged: true
  },
  [JobStatus.Canceled]: {
    summary: 'Canceled',
    detail: 'Your job has been canceled.',
    noCreditCharged: true
  },
  [JobStatus.ExecutionTimeout]: {
    summary: 'Took Too Long',
    detail:
      'Your job took longer than expected and was stopped. Try simplifying your workflow or reducing the output size.'
  },
  [JobStatus.ForceTimeout]: {
    summary: 'Took Too Long',
    detail:
      'Your job exceeded the maximum allowed time. Try simplifying your workflow or reducing the output size.'
  },
  [JobStatus.WorkerShutdownError]: {
    summary: 'Session Interrupted',
    detail:
      'Your job was interrupted due to a temporary issue. Please try again.',
    noCreditCharged: true
  },
  [JobStatus.UnknownError]: {
    summary: 'Something Went Wrong',
    detail:
      'An unexpected issue occurred. Please try again. If this persists, contact support.',
    noCreditCharged: true
  }
}

/**
 * Extract a human-readable error message from a failed Job.
 * Checks executeException, validateException, promptResult.error, message in order.
 * Falls back to user-friendly message by JobStatus.
 */
//!nordy - extract error message from job for toast display
function extractJobErrorMessage(job: Job): {
  summary: string
  detail: string
} {
  const fallback: JobErrorMessage = JOB_ERROR_MESSAGES[job.status] ?? {
    summary: 'Error',
    detail: `Job failed with status: ${job.status}`
  }

  let detail: string | undefined
  if (job.executeException) {
    detail = `${job.executeException.exception_type}: ${job.executeException.exception_message}`
  } else if (job.validateException) {
    detail = job.validateException.message || job.validateException.type
  } else if (job.promptResult?.error) {
    const err = job.promptResult.error
    detail = typeof err === 'string' ? err : err.message || err.type
  } else if (job.message) {
    detail = job.message
  }

  const resolvedDetail = detail || fallback.detail
  return {
    summary: fallback.summary,
    detail: fallback.noCreditCharged
      ? `${resolvedDetail} ${NO_CREDIT_CHARGED_NOTICE}`
      : resolvedDetail
  }
}

/**
 * Build ResultNordyQueue[] from output assets.
 * Shared logic for Done/Error/Timeout status handling.
 */
function buildResultQueues(
  outputAssets: OutputAsset<NordyUser, string, ImageAsset>[]
): ResultNordyQueue[] {
  return outputAssets.map((outputAsset) => {
    const asset = outputAsset.asset
    return new ResultNordyQueue({
      filename: typeof asset === 'string' ? undefined : asset?.key,
      assetId: typeof asset === 'string' ? asset : asset?._id,
      outputAssetId: outputAsset._id,
      url:
        fileUrlByAsset({
          asset: typeof asset === 'string' ? undefined : asset,
          quality: 'raw'
        }) ?? undefined,
      nodeId: '',
      type: 'output',
      assetKind: outputAsset.kind,
      isPreview: outputAsset.isPreview
    })
  })
}

export const useNordyQueueStore = defineStore('nordyQueue', () => {
  const nordyUserStore = useNordyUserStore()

  const timer = ref(0)
  const timerInterval = ref<ReturnType<typeof setInterval> | null>(null)
  const lastJobId = ref<string>('')

  const isLoading = ref(false)
  const tasks = ref<NordyQueue[]>([])

  /**
   * 태스크 완료 대기 resolver 맵.
   * 순차 실행을 위해 Q1 완료 후 Q2 Job을 생성하는 데 사용.
   */
  const taskCompletionResolvers = new Map<string, Array<() => void>>()

  const flatTasks = computed<NordyQueue[]>(() =>
    tasks.value.flatMap((task: NordyQueue) => task.flatten())
  )

  const stopTimer = () => {
    if (timerInterval.value) {
      clearInterval(timerInterval.value)
      timerInterval.value = null
    }
  }

  const updateTaskToQueued = ({ jobId }: { jobId: string }) => {
    const task = [...tasks.value]
      .reverse()
      .find((task) => task.displayStatus === NordyTaskDisplayStatus.Pending)
    if (task) {
      task.taskType = 'Queued'
      task.jobId = jobId
      lastJobId.value = jobId
    }
  }

  const updateTaskToRunning = async ({ job }: { job: Job }) => {
    const task = tasks.value.find((task) => task.jobId === job._id)
    if (task) {
      task.job = job
      task.taskType = 'Running'
    }
    return job
  }

  const deleteTask = (queueId: string) => {
    tasks.value = tasks.value.filter((task) => task.id !== queueId)
  }

  const clear = () => {
    // Pending만 제거. Queued/Running은 이미 백엔드 Job이 존재하므로 그대로 유지.
    const tasksToRemove = tasks.value.filter(
      (task) => task.displayStatus === NordyTaskDisplayStatus.Pending
    )

    for (const task of tasksToRemove) {
      // 순차 루프가 즉시 빠져나갈 수 있도록 대기 중인 resolver 해제
      const resolvers = taskCompletionResolvers.get(task.id)
      if (resolvers) {
        resolvers.forEach((r) => r())
        taskCompletionResolvers.delete(task.id)
      }
    }

    tasks.value = tasks.value.filter(
      (task) => task.displayStatus !== NordyTaskDisplayStatus.Pending
    )
  }

  /**
   * 태스크가 History(완료/실패/취소)로 전환될 때 resolve되는 Promise 반환.
   * 순차 배치 처리에 사용.
   */
  const waitForTaskCompletion = (taskId: string): Promise<void> => {
    return new Promise((resolve) => {
      const task = tasks.value.find((t) => t.id === taskId)
      if (!task || task.isHistory) {
        resolve()
        return
      }
      const resolvers = taskCompletionResolvers.get(taskId) ?? []
      resolvers.push(resolve)
      taskCompletionResolvers.set(taskId, resolvers)
    })
  }

  const pendingTasks = computed(() => {
    return tasks.value.filter(
      (task) =>
        task.displayStatus === NordyTaskDisplayStatus.Pending ||
        task.displayStatus === NordyTaskDisplayStatus.Queued
    )
  })

  const hasPendingTasks = computed(() => {
    return pendingTasks.value.length > 0
  })

  const queuedTask = computed(() => {
    return tasks.value.find((task) => task.isQueued)
  })

  const runningTask = computed(() => {
    return tasks.value.find((task) => task.isRunning)
  })

  const historyTasks = computed(() => {
    return tasks.value.filter((task) => task.isHistory)
  })

  const queueCount = computed(() => {
    return {
      pending: tasks.value.filter(
        (task) => task.displayStatus === NordyTaskDisplayStatus.Pending
      ).length,
      running: tasks.value.filter(
        (task) => task.displayStatus === NordyTaskDisplayStatus.Running
      ).length,
      count: tasks.value.filter(
        (task) =>
          task.displayStatus === NordyTaskDisplayStatus.Pending ||
          task.displayStatus === NordyTaskDisplayStatus.Queued ||
          task.displayStatus === NordyTaskDisplayStatus.Running
      ).length
    }
  })

  /** =======================================================
   * 대기열 관리
   * ========================================================= */
  const queuePosition = ref<number | null>(null)
  const updateQueuePosition = async (count: number | null) => {
    queuePosition.value = count
  }

  /** =======================================================
   * Job 상태 폴링
   * ========================================================= */
  const JOB_POLLING_INTERVAL = 2000
  let jobPollingInterval: ReturnType<typeof setInterval> | null = null

  /**
   * Merge outputs with deduplication using es-toolkit uniqBy.
   */
  function mergeOutputs(
    existing: ResultNordyQueue[] | ReadonlyArray<ResultNordyQueue> | undefined,
    newItems: ResultNordyQueue[]
  ): ResultNordyQueue[] {
    return uniqBy(
      [...(existing || []), ...newItems],
      (item) => item.outputAssetId
    )
  }

  /**
   * Finalize a task as History with given status and outputs.
   * 멱등성 보장: 이미 History인 태스크는 outputs 병합만 수행하고 cleanup은 건너뛴다.
   */
  async function finalizeTask(
    currentTask: NordyQueue,
    statusStr: 'success' | 'error' | 'execution_timeout',
    resultQueues: ResultNordyQueue[] = []
  ) {
    const alreadyFinalized = currentTask.taskType === 'History'

    if (resultQueues.length > 0) {
      currentTask.outputs = mergeOutputs(currentTask.outputs, resultQueues)
      currentTask.flatOutputs = mergeOutputs(
        currentTask.flatOutputs,
        resultQueues
      )
    }

    if (alreadyFinalized) {
      return
    }

    currentTask.status = {
      status_str: statusStr,
      completed: true,
      messages: []
    }
    currentTask.taskType = 'History'

    // 이 태스크에 대한 waitForTaskCompletion 대기 Promise 해제
    const resolvers = taskCompletionResolvers.get(currentTask.id)
    if (resolvers) {
      resolvers.forEach((r) => r())
      taskCompletionResolvers.delete(currentTask.id)
    }

    outputUpdateByTask(currentTask)
    //!nordy - job 완료 시 에셋 사이드바 갱신 (output 목록 최신화)
    if (statusStr === 'success' || statusStr === 'execution_timeout') {
      void useAssetsStore().updateHistory()
    }
    await processCleanup()
  }

  //!nordy - Done 직후 outputAsset 페치가 일시 실패할 때 폴링 틱마다 재시도할 최대 횟수 (2회 ≈ 4초)
  const OUTPUT_ASSET_FETCH_MAX_RETRIES = 2

  watch(
    runningTask,
    async (currentTask) => {
      // 기존 interval이 있으면 먼저 정리 (메모리 누수 방지)
      if (jobPollingInterval !== null) {
        clearInterval(jobPollingInterval)
        jobPollingInterval = null
      }

      if (!currentTask || !currentTask.jobId) return

      // 태스크 러닝 시 대기열 초기화
      queuePosition.value = null

      // 타이머 시작: 매초 증가
      timer.value = 0
      timerInterval.value = setInterval(() => {
        timer.value++
      }, 1000)

      //!nordy - 진행 중 상태만 명시하고, 그 외는 모두 종료 처리 (미처리 상태로 타이머가 무한 진행되는 버그 방지)
      const IN_PROGRESS_STATUSES: Set<JobStatus> = new Set([
        JobStatus.Connecting,
        JobStatus.Connected,
        JobStatus.Running
      ])

      //!nordy - 폴링 콜백 동시 실행 방지 가드 (콜백이 2초 이상 걸릴 경우 setInterval이 중첩 발사하는 것을 차단)
      let inFlight = false
      //!nordy - Done 상태에서 outputAsset 페치가 throw로 실패한 횟수. 한도 초과 시 'error' finalize로 강제 전환
      let outputFetchFailures = 0

      jobPollingInterval = setInterval(async () => {
        // 이전 폴링이 아직 진행 중이면 이번 틱은 스킵
        if (inFlight) return
        // 이미 finalize되었으면 더 이상 폴링하지 않음
        if (currentTask.taskType !== 'Running') return

        inFlight = true
        try {
          const job = await nordyApi.getJobById(currentTask.jobId!)

          // 응답 도착 사이에 finalize되었거나 다른 task로 전환된 경우 중단
          if (currentTask.taskType !== 'Running') return

          const jobStatus = job.status
          currentTask.job = job

          // 진행 중 상태면 다음 폴링까지 대기
          if (IN_PROGRESS_STATUSES.has(jobStatus)) return

          if (jobStatus === JobStatus.Done) {
            //!nordy - 빈 배열은 정상 케이스(SaveImage 미사용, 캐시 히트 등). throw일 때만 실패로 간주하여 재시도.
            let outputAssets
            try {
              outputAssets = await nordyApi.getOutputAssetByJobId(
                currentTask.jobId!
              )
            } catch (fetchError) {
              outputFetchFailures++
              console.error('[nordyQueueStore] getOutputAssetByJobId failed', {
                jobId: currentTask.jobId,
                attempt: outputFetchFailures,
                error: fetchError
              })
              if (outputFetchFailures < OUTPUT_ASSET_FETCH_MAX_RETRIES) {
                return // 다음 폴링 틱에서 재시도
              }
              // 한도 초과: success로 종료하고 사용자에게 안내 (Job 자체는 성공이므로 history에서 새로고침으로 복구 가능)
              await finalizeTask(currentTask, 'success', [])
              await nextTick()
              useToastStore().add({
                severity: 'warn',
                summary: 'Output Loading Issue',
                detail:
                  'Your job completed successfully, but the output is taking longer than usual to load. Please check your history.',
                life: 10000
              })
              return
            }

            await finalizeTask(
              currentTask,
              'success',
              buildResultQueues(outputAssets)
            )
            return
          }

          if (jobStatus === JobStatus.ExecutionTimeout) {
            //!nordy - timeout은 부분 산출물이 의미있을 수 있으므로 best-effort로 페치, 실패해도 빈 배열로 진행
            let timeoutOutputs: OutputAsset<NordyUser, string, ImageAsset>[] =
              []
            try {
              timeoutOutputs = await nordyApi.getOutputAssetByJobId(
                currentTask.jobId!
              )
            } catch (fetchError) {
              console.error(
                '[nordyQueueStore] getOutputAssetByJobId failed (timeout path)',
                fetchError
              )
            }
            await finalizeTask(
              currentTask,
              'execution_timeout',
              buildResultQueues(timeoutOutputs)
            )
            //!nordy - show timeout toast after nextTick so removeAllGroups() finishes first
            const timeoutMsg = extractJobErrorMessage(job)
            await nextTick()
            useToastStore().add({
              severity: 'warn',
              summary: timeoutMsg.summary,
              detail: timeoutMsg.detail,
              life: 10000
            })
            return
          }

          // 진행 중도, Done도, Timeout도 아닌 모든 상태 → 에러 처리
          await finalizeTask(currentTask, 'error')
          //!nordy - show error toast after nextTick so removeAllGroups() finishes first
          const errorMsg = extractJobErrorMessage(job)
          await nextTick()
          useToastStore().add({
            severity: 'error',
            summary: errorMsg.summary,
            detail: errorMsg.detail,
            life: 10000
          })
        } catch (error) {
          console.error('[jobPolling] error:', error)
        } finally {
          inFlight = false
        }
      }, JOB_POLLING_INTERVAL)
    },
    { immediate: true }
  )

  const processCleanup = async () => {
    if (jobPollingInterval !== null) {
      clearInterval(jobPollingInterval)
    }
    jobPollingInterval = null
    stopTimer()
    useCanvasStore().getCanvas().draw(true, true)
    //!nordy - 크레딧 갱신 후 잔액 검사
    const { success } = await nordyUserStore.getActiveCredit()
    if (success && nordyUserStore.credit.totalAmount <= 0) {
      useNordyModalStore().openPurchaseModal()
    }

    api.closeSocket()
    useToastStore().removeAll()
  }

  const outputUpdateByTask = (task: NordyQueue) => {
    if (task.workflowUid && task.outputs?.length) {
      const workflowStore = useNordyWorkflowStore()
      const matchingWorkflow = workflowStore.workflows.find(
        (workflow) => workflow.uid === task.workflowUid
      )

      if (matchingWorkflow) {
        const lastImageOutput = task.outputs
          .filter((output) => output.isImage && !output.isPreview)
          .pop()

        if (lastImageOutput) {
          matchingWorkflow.lastOutputAsset = lastImageOutput
        }
      }
    }
  }

  /** =======================================================
   * History loading from API
   * ========================================================= */
  const historyLoaded = ref(false)

  const loadHistory = async ({ page = 1, limit = 30 } = {}) => {
    if (isLoading.value) return
    isLoading.value = true

    try {
      const jobs = await nordyApi.getComfyJobHistory({ page, limit })

      const historyQueues: NordyQueue[] = jobs.map((job) => {
        const jobStatus = job.status
        let statusStr: 'success' | 'error' | 'execution_timeout' = 'error'
        if (jobStatus === JobStatus.Done) {
          statusStr = 'success'
        } else if (jobStatus === JobStatus.ExecutionTimeout) {
          statusStr = 'execution_timeout'
        }

        const outputAssets =
          (
            job as Job & {
              outputAsset?: OutputAsset<NordyUser, string, ImageAsset>[]
            }
          ).outputAsset ?? []
        const outputs = buildResultQueues(outputAssets)

        const queue = new NordyQueue({
          id: job._id,
          taskType: 'History',
          workflow: null,
          prompt: { workflow: null, output: null },
          status: {
            status_str: statusStr,
            completed: true,
            messages: []
          },
          outputs,
          flatOutputs: outputs,
          job,
          jobId: job._id
        })
        queue.createdAt = new Date(job.createdAt)
        return queue
      })

      // Merge: keep session tasks, add history that aren't duplicates
      const existingJobIds = new Set(
        tasks.value.filter((t) => t.isHistory).map((t) => t.jobId)
      )

      const newHistoryQueues = historyQueues.filter(
        (q) => !existingJobIds.has(q.jobId)
      )

      if (newHistoryQueues.length > 0) {
        tasks.value = [...tasks.value, ...newHistoryQueues]
      }

      historyLoaded.value = true
    } catch (error) {
      console.error('[nordyQueueStore] loadHistory error:', error)
    } finally {
      isLoading.value = false
    }
  }

  return {
    queueCount,
    isLoading,
    tasks,
    flatTasks,
    updateTaskToQueued,
    updateTaskToRunning,
    deleteTask,
    clear,
    pendingTasks,
    hasPendingTasks,
    runningTask,
    historyTasks,
    timer,
    timerInterval,
    lastJobId,
    queuePosition,
    queuedTask,
    updateQueuePosition,
    waitForTaskCompletion,
    historyLoaded,
    loadHistory
  }
})
